import Phaser from 'phaser';
import * as C from 'tower/constants';
import {
  State,
  StateMachine,
  Direction,
  directionalVelocity,
  gridMove,
  oppositeDirection,
  wait,
  frameMs,
  asyncTween,
  relativeMove,
  setVelocityTowards,
  steppedEase,
  getOrientation,
  Orientation,
} from 'tower/util';
import BattleScene from 'tower/scenes/battle';
import gameState from 'tower/state/game-state';
import FlashableSprite from 'tower/flashable';
import { HitOptions, WorkerMessage, WorkerReceiverMessage } from 'tower/types';
import Room from './room';
import { EnemyOptions } from './enemy';

export default class OtherManager {
  other: Other;

  constructor(
    private scene: BattleScene,
    private room: Room,
    private sharedWorker: SharedWorker,
  ) {
    sharedWorker.port.addEventListener('message', this.handleMessage);

    this.other = new Other(scene, room, 0, 0);
    this.other.disable();
    this.other.on('hit', this.handleOtherHit);

    scene.player.on('idle', this.handlePlayerIdle);
    scene.player.on('moving', this.handlePlayerMoving);
    scene.player.on('sword', this.handlePlayerSword);
  }

  enterRoom() {
    this.sendMessage({ type: 'enterRoom' });
  }

  exitRoom() {
    this.sendMessage({ type: 'exitRoom' });
  }

  sendMessage(msg: WorkerMessage) {
    this.sharedWorker.port.postMessage(msg);
  }

  positionOther(x: number, y: number, direction: Direction) {
    // Mirror y coordinates across room centerline
    const center = this.room.y + this.room.height / 2;
    const diffCenter = y - center;
    this.other.setPosition(x, y + diffCenter * -2);
    this.other.direction =
      getOrientation(direction) === Orientation.Vertical ? oppositeDirection(direction) : direction;
  }

  handleMessage = (event: MessageEvent<WorkerReceiverMessage>) => {
    const { other, scene } = this;
    const msg = event.data;

    switch (msg.type) {
      case 'otherPosition': {
        if (!other.visible) {
          other.stateMachine.transition('idle');
          other.enable();
        }
        this.positionOther(msg.x, msg.y, msg.direction);
        break;
      }

      case 'enterRoom': {
        other.enable();
        if (document.visibilityState === 'visible') {
          other.stateMachine.transition('enter');
        } else {
          other.setPosition(this.room.doors.up!.x, this.room.doors.up!.y + 16);
        }
        break;
      }

      case 'exitRoom': {
        other.disable();
        break;
      }

      case 'otherIdle': {
        this.positionOther(msg.x, msg.y, msg.direction);
        other.stateMachine.transition('idle');
        break;
      }

      case 'otherMoving': {
        this.positionOther(msg.x, msg.y, msg.direction);

        if (document.visibilityState === 'visible') {
          // Mirror up and down
          let direction = msg.direction;
          if (getOrientation(direction) === Orientation.Vertical) {
            direction = oppositeDirection(direction);
          }

          other.stateMachine.transition('moving', direction);
        }
        break;
      }

      case 'otherSword': {
        this.positionOther(msg.x, msg.y, msg.direction);
        if (document.visibilityState === 'visible') {
          other.stateMachine.transition('sword');
        }
        break;
      }

      case 'otherHit': {
        // Need to reverse direction if vertical because player and other are mirrored
        let directionOverride = msg.options.directionOverride!;
        if (getOrientation(directionOverride) === Orientation.Vertical) {
          directionOverride = oppositeDirection(directionOverride);
        }
        const options = { ...(msg.options as HitOptions), directionOverride };

        scene.player.hit(options, document.visibilityState === 'hidden');
        if (gameState.hp <= 0) {
          this.sendMessage({ type: 'playerDeath' });
          scene.scene.stop('hud');
          scene.scene.switch('permadeath');
        } else {
          // Retain original direction for the other since it came from them in the first place.
          this.sendMessage({ type: 'playerHurt', options: msg.options });
        }
        break;
      }

      case 'otherHurt': {
        if (document.visibilityState === 'visible') {
          other.stateMachine.transition('hurt', msg.options);
        }
        break;
      }

      case 'otherDeath': {
        if (document.visibilityState === 'visible') {
          other.stateMachine.transition('death');
        } else {
          other.setVisible(false);
          other.body.setEnable(false);
          scene.events.emit('otherdeath', other);
        }
        break;
      }
    }
  };

  handleOtherHit = (options: HitOptions) => {
    this.sendMessage({
      type: 'otherHit',
      options: { weaponType: options.weaponType, damage: options.damage, directionOverride: options.directionOverride },
    });
  };

  handlePlayerIdle = () => {
    const { player } = this.scene;
    this.sendMessage({ type: 'otherIdle', direction: player.direction, x: player.x, y: player.y });
  };

  handlePlayerMoving = (direction: Direction) => {
    const { player } = this.scene;
    this.sendMessage({ type: 'otherMoving', direction, x: player.x, y: player.y });
  };

  handlePlayerSword = () => {
    const { player } = this.scene;
    this.sendMessage({ type: 'otherSword', direction: player.direction, x: player.x, y: player.y });
  };

  destroy() {
    const { scene, sharedWorker } = this;
    sharedWorker.port.removeEventListener('message', this.handleMessage);
    scene.player.off('idle', this.handlePlayerIdle);
    scene.player.off('moving', this.handlePlayerMoving);
    scene.player.off('sword', this.handlePlayerSword);
  }
}

export class Other extends FlashableSprite {
  stateMachine: StateMachine<OtherState>;
  declare body: Phaser.Physics.Arcade.Body;
  invulnerable = false;
  enemyOptions: EnemyOptions = {
    solid: true,
    passthroughDoors: true,
  };
  touchDamage = null;

  private _direction: Direction = Direction.Left;

  constructor(
    public scene: BattleScene,
    public room: Room,
    x: number,
    y: number,
  ) {
    super(scene, x, y, 'link', 0);
    scene.add.existing(this);
    scene.physics.add.existing(this);

    room.enemies.add(this);
    room.colliders.push(scene.physics.add.collider(this, room.maze.collisionLayer));
    room.colliders.push(scene.physics.add.collider(this, scene.player));
    room.colliders.push(scene.physics.add.collider(this, room.doors.down!.doorBody));

    this.setDepth(C.DEPTHS.Player);
    this.body.setSize(8, 8).setOffset(4, 6);
    this.body.pushable = false;

    this.direction = Direction.Down;

    this.stateMachine = new StateMachine('idle', {
      idle: new IdleState(scene, this),
      moving: new MovingState(scene, this),
      sword: new SwordState(scene, this),
      enter: new EnterState(scene, this),
      hurt: new HurtState(scene, this),
      death: new DeathState(scene, this),
    });
  }

  enable() {
    this.setVisible(true);
    this.body.setEnable(true);
  }

  disable() {
    this.setVisible(false);
    this.body.setEnable(false);
  }

  get palette() {
    return this._palette + (gameState.hasItem('blueTunic') ? 4 : 0) + (gameState.hasItem('betterShield') ? 8 : 0);
  }

  set palette(value: number) {
    super.palette = value;
  }

  get direction() {
    return this._direction;
  }

  set direction(newDirection: Direction) {
    this._direction = newDirection;
    if (newDirection === Direction.Left) {
      this.setFlipX(true);
    } else {
      this.setFlipX(false);
    }
  }

  hit(options: HitOptions) {
    if (this.invulnerable) {
      return;
    }

    this.emit('hit', options);
  }

  showSwing() {
    switch (this.direction) {
      case Direction.Up:
        this.setFrame(8);
        break;
      case Direction.Down:
        this.setFrame(6);
        break;
      case Direction.Right:
        this.setFrame(7);
        break;
      case Direction.Left:
        this.setFrame(7);
        break;
    }
  }

  update() {
    const { scene, room } = this;
    const door = room.doors.up!;
    const touchingDoor = scene.physics.overlap(this, door.doorBody);
    if (touchingDoor && door.state !== 'open') {
      scene.sound.play('openDoor');
      door.state = 'open';
    } else if (!touchingDoor && door.state !== 'closed') {
      scene.sound.play('openDoor');
      door.state = 'closed';
    }

    this.stateMachine.update();
  }
}

class OtherState extends State {
  constructor(
    protected scene: BattleScene,
    protected other: Other,
  ) {
    super();
  }
}

class EnterState extends OtherState {
  async handleEntered() {
    const { scene, other } = this;

    const door = other.room.doors.up!;

    // Position player under door and walk in
    other.body.reset(door.x, door.y);
    other.enable();
    other.direction = Direction.Down;

    const otherMoveDistance = 16;
    const [px, py] = gridMove(other.x, other.y, Direction.Down, otherMoveDistance);
    other.play(`playerWalk[down]`);
    await asyncTween(scene, {
      targets: [other],
      x: px,
      y: py,
      duration: frameMs(16),
      ease: steppedEase(otherMoveDistance),
    });

    return this.transition('idle');
  }
}

class IdleState extends OtherState {
  handleEntered() {
    this.other.stop();
    this.other.body.setVelocity(0);

    let frame = 4;
    switch (this.other.direction) {
      case Direction.Left:
      case Direction.Right:
        frame = 2;
        break;
      case Direction.Down:
        frame = 0;
        break;
    }
    this.other.setFrame(frame);
  }
}

class MovingState extends OtherState {
  direction!: Direction;

  handleEntered(direction?: Direction) {
    const { other } = this;

    // Edge case when returning from hurt
    if (!direction) {
      return this.transition('idle');
    }

    this.direction = direction;
    other.direction = direction;
    other.play(`playerWalk[${direction}]`);
    other.body.setVelocity(...directionalVelocity(C.PLAYER_VELOCITY, direction));
  }

  update() {
    const { other } = this;
    other.body.setVelocity(...directionalVelocity(C.PLAYER_VELOCITY, this.direction));
  }
}

class SwordState extends OtherState {
  async handleEntered() {
    const swordKey = gameState.sword;
    if (swordKey === null) {
      return this.transition('idle');
    }

    const { scene, other } = this;
    other.stop();
    other.body.setVelocity(0);

    const swordVariant = C.SWORD_VARIANTS[swordKey];
    const sword = scene.add
      .sprite(other.x, other.y, 'sword', swordVariant.frame)
      .setDepth(C.DEPTHS.Sword)
      .setVisible(false);
    relativeMove(sword, other.direction, 14);
    other.room.misc.add(sword);

    other.showSwing();
    switch (other.direction) {
      case Direction.Down:
        sword.setRotation(Phaser.Math.DegToRad(180));
        break;
      case Direction.Right:
        sword.setFrame(swordVariant.frame + 1);
        break;
      case Direction.Left:
        sword.setFrame(swordVariant.frame + 1).setFlipX(true);
        break;
    }

    await wait(scene, frameMs(3));
    sword.setVisible(true);
    scene.sound.play('sword');

    await wait(scene, frameMs(6));

    const [dx, dy] = gridMove(0, 0, oppositeDirection(other.direction), 10);
    await asyncTween(scene, {
      targets: sword,
      x: `+=${dx}`,
      y: `+=${dy}`,
      duration: frameMs(3),
      ease: Phaser.Math.Easing.Stepped,
    });

    sword.destroy();

    return this.transition('idle');
  }
}

class HurtState extends OtherState {
  async handleEntered({ directionOverride }: HitOptions) {
    const { other, scene } = this;
    other.invulnerable = true;
    other.flash(6).on('complete', () => {
      other.invulnerable = false;
    });

    scene.sound.play('playerHurt');

    const pushTowards = directionOverride!;
    setVelocityTowards(other.body, pushTowards, C.PLAYER_PUSH_SPEED);
    await wait(scene, frameMs(C.PLAYER_PUSH_FRAMES));

    other.body.setVelocity(0);
    return this.stateMachine.transition('idle');
  }
}

class DeathState extends OtherState {
  handleEntered() {
    const { scene, other } = this;
    const explosion = scene.add
      .sprite(other.x, other.y, 'enemies', 11)
      .setDepth(C.DEPTHS.Enemies)
      .play('enemyDeath')
      .once('animationcomplete', () => explosion.destroy());
    scene.sound.play('enemyDeath');
    other.setVisible(false);
    other.body.setEnable(false);

    scene.events.emit('otherdeath', other);
  }
}
